import { useCallback, useRef } from 'react';

import { ProcessedLogsInterface } from '../types';
import styles from '../LogList/LogRow/LogRow.module.css';

// Matches the line-height of a single unwrapped row in LogRow.module.css. Also used as
// the virtualiser's estimate for rows it has not measured yet, so the two must agree.
export const DEFAULT_ROW_SIZE = 25;

export function useSizeCache() {
  const sizeCache = useRef<Record<string, number>>({});
  // Reusable hidden measurement nodes to avoid reflows and ensure consistent computed styles.
  const measureRef = useRef<{
    parent?: HTMLDivElement; // real list outer element (attach measuring DOM here)
    container?: HTMLDivElement; // hidden off-screen container used for measurement
    pre?: HTMLPreElement; // <pre> styled like a real log row
    lineNumber?: HTMLDivElement; // gutter that simulates line numbers
    content?: HTMLDivElement; // holds the log text to measure height
  }>({});

  const computeRowHeight = useCallback(
    (
      logLine: string,
      outerRef: React.RefObject<HTMLDivElement>,
      isTextWrapped: boolean,
      showLineNumbers: boolean
    ) => {
      const parent = outerRef.current;
      if (!parent) {
        return undefined;
      }

      const refs = measureRef.current;

      if (refs.parent !== parent) {
        if (!refs.container) {
          // Hidden container anchored under the list so wrapped height matches real layout.
          const container = document.createElement('div');
          // Off-screen (not display:none) so layout and metrics are still computed.
          container.style.position = 'absolute';
          container.style.left = '-10000px';
          container.style.top = '0';
          container.style.visibility = 'hidden';
          container.style.pointerEvents = 'none';

          // Create the same dom structure as the real LogRow component
          const pre = document.createElement('pre');
          // Mirror row CSS to match fonts, padding, and line-height.
          pre.className = styles.logRow;

          const lineNumber = document.createElement('div');
          lineNumber.className = styles.lineNumber;
          lineNumber.innerText = '1';

          const content = document.createElement('div');

          pre.appendChild(lineNumber);
          pre.appendChild(content);
          container.appendChild(pre);

          refs.container = container;
          refs.pre = pre;
          refs.lineNumber = lineNumber;
          refs.content = content;
        }

        // Attach under the same parent to inherit the exact CSS cascade and width.
        parent.appendChild(refs.container);
        refs.parent = parent;
      }

      const { container, pre, lineNumber, content } = refs;
      if (!container || !pre || !lineNumber || !content) {
        return undefined;
      }

      // Match available width so wrapping reflects the on-screen layout.
      const effectiveWidth = parent?.clientWidth ?? 600;
      container.style.width = `${effectiveWidth}px`;
      // Reflect wrap mode to measure wrapped vs single-line heights.
      pre.style.whiteSpace = isTextWrapped ? 'pre-wrap' : 'pre';
      // Include/exclude gutter to account for line-number width.
      lineNumber.style.display = showLineNumbers ? '' : 'none';
      content.innerText = logLine;

      return pre.clientHeight;
    },
    []
  );

  const getSize = useCallback(
    (
      index: number,
      logs: ProcessedLogsInterface,
      outerRef: React.RefObject<HTMLDivElement>,
      isTextWrapped: boolean,
      showLineNumbers: boolean
    ) => {
      const log = logs.logs[index]?.lowerLine;
      if (!log) {
        return DEFAULT_ROW_SIZE;
      }

      const width = outerRef.current?.clientWidth || 0;
      const sizeKey = makeSizeKey(log, isTextWrapped, showLineNumbers, width);
      const cachedSize = sizeCache.current[sizeKey];

      if (cachedSize) {
        return cachedSize;
      }

      const rowHeight = computeRowHeight(
        log,
        outerRef,
        isTextWrapped,
        showLineNumbers
      );

      if (rowHeight) {
        // only cache if the row height is a valid number
        sizeCache.current[sizeKey] = rowHeight;
        return rowHeight;
      }

      return DEFAULT_ROW_SIZE;
    },
    [computeRowHeight]
  );

  const resetCache = useCallback(() => {
    sizeCache.current = {};
  }, []);

  return { getSize, resetCache };
}

export function makeSizeKey(
  lowerLine: string,
  isTextWrapped: boolean,
  showLineNumbers: boolean,
  width: number
): string {
  // using the width directly is ok, as the cache is cleared on resize (with a small debounce)
  return `${lowerLine
    .split('\n')
    .map((line) => line.length)
    .join('-')}-${isTextWrapped}-${showLineNumbers}-${width}`;
}
